// AssoIntegrations.jsx — Configuration des intégrations propres à chaque asso
// /dashboard/integrations · Président uniquement
//   • Brevo : clé API + email expéditeur + nom expéditeur (pour envoyer aux membres)
//   • Stripe Connect : compte de l'asso (pour encaisser adhésions / billetterie)
// Tant que les clés globales (Osez Studio) ne sont pas en .env, les boutons "Test" et "Onboarding"
// sont MOCKED — ils valident le format et stockent en BDD, sans appel réel à Brevo/Stripe.
import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';
import {
  Loader2, Lock, Mail, CreditCard, CheckCircle2, AlertCircle, ExternalLink, Send, Info, KeyRound, Save,
  FileText, Ticket, Repeat,
} from 'lucide-react';

export default function AssoIntegrations() {
  const { user } = useAuth();
  const [asso, setAsso] = useState(null);
  const [modules, setModules] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [a, m] = await Promise.all([api.get('/assos/me'), api.get('/assos/modules').catch(() => ({ data: null }))]);
      setAsso(a.data); setModules(m.data);
    } catch { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="p-8 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  if (user.role !== 'president') {
    return (
      <div className="p-8 max-w-2xl mx-auto" data-testid="integrations-denied">
        <div className="card text-center py-12">
          <Lock className="w-8 h-8 mx-auto mb-3 text-[#CBD5E1]" />
          <h3 className="font-display text-lg font-bold text-[#0F172A] mb-2">Accès réservé au président</h3>
          <p className="text-sm text-[#475569]">Seul le président de l'association peut configurer les modules et intégrations.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-3xl mx-auto" data-testid="integrations-page">
      <div className="mb-6 slide-up">
        <div className="label-uppercase mb-2" style={{ color: 'var(--theme-primary)' }}>Paramètres</div>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-[#0F172A]">Modules & Intégrations</h1>
        <p className="text-sm text-[#475569] mt-2">Connectez vos comptes <strong>Brevo</strong> (emails) et <strong>Stripe</strong> (paiements), puis activez les modules qui vous intéressent.</p>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6 flex items-start gap-3" data-testid="integrations-info">
        <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
        <div className="text-sm text-blue-900">
          <strong>Pourquoi vos propres comptes ?</strong> Vos emails partent depuis votre adresse (pas la nôtre) et vos paiements arrivent directement sur votre compte. AssoPerf ne fait que connecter ces services à votre espace.
        </div>
      </div>

      <div className="label-uppercase mb-3 text-[#94A3B8]">Intégrations à connecter</div>
      <BrevoCard asso={asso} onChanged={load} />
      <StripeCard asso={asso} onChanged={load} />

      {modules && (
        <>
          <div className="label-uppercase mb-3 mt-8 text-[#94A3B8]">Modules de l'association</div>
          <ModulesSection modules={modules} onChanged={load} />
        </>
      )}

      <div className="mt-8 text-center text-xs text-[#94A3B8]">
        Besoin d'aide ? <a href="mailto:support@assoperf.fr" className="underline">support@assoperf.fr</a>
      </div>
    </div>
  );
}

// ─── MODULES (à activer) ────────────────────────────────────────────────────
const MODULE_META = {
  recus_pdf: { icon: FileText, name: 'Reçus PDF', desc: 'Génère et envoie un reçu de cotisation PDF après chaque paiement' },
  billetterie_en_ligne: { icon: Ticket, name: 'Paiement Billetterie', desc: 'Encaisse les billets en ligne sur votre compte Stripe' },
  prelevement_3x_auto: { icon: Repeat, name: 'Prélèvement 3× auto', desc: 'Prélève automatiquement les échéances suivantes du paiement en 3×' },
};

function ModulesSection({ modules, onChanged }) {
  const stripeOk = !!modules?.paiement_stripe?.active;
  const toggle = async (module, enabled) => {
    try {
      await api.put('/assos/modules', { module, enabled });
      toast.success(enabled ? 'Module activé' : 'Module désactivé');
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };
  return (
    <div className="space-y-3">
      {Object.keys(MODULE_META).map((id) => (
        <ModuleToggle key={id} id={id} active={!!modules?.[id]?.active} disabled={!stripeOk} onToggle={toggle} />
      ))}
    </div>
  );
}

function ModuleToggle({ id, active, disabled, onToggle }) {
  const meta = MODULE_META[id];
  const Icon = meta.icon;
  return (
    <div className={`card flex items-center gap-4 ${disabled ? 'opacity-60' : ''}`} data-testid={`module-${id}`}>
      <div className="w-11 h-11 rounded-xl grid place-items-center shrink-0 bg-[#F1F5F9]" style={{ color: 'var(--theme-primary)' }}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-bold text-[#0F172A] text-sm">{meta.name}</div>
        <div className="text-xs text-[#475569]">{meta.desc}</div>
        {disabled && <div className="text-[11px] text-amber-600 mt-1">Activez d'abord le paiement Stripe</div>}
      </div>
      <button
        type="button" disabled={disabled} onClick={() => onToggle(id, !active)}
        className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${active ? '' : 'bg-[#CBD5E1]'} ${disabled ? 'cursor-not-allowed' : ''}`}
        style={active && !disabled ? { background: 'var(--theme-primary)' } : {}}
        data-testid={`module-toggle-${id}`} role="switch" aria-checked={active}
      >
        <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${active ? 'translate-x-5' : ''}`} />
      </button>
    </div>
  );
}

// ─── BREVO CARD ─────────────────────────────────────────────────────────────
function BrevoCard({ asso, onChanged }) {
  const brevo = asso.integrations?.brevo || {};
  const configured = brevo.configured;
  const [form, setForm] = useState({
    api_key: '', from_email: brevo.from_email || asso.email_contact || '', from_name: brevo.from_name || asso.nom || '',
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    if (!form.api_key && !configured) { toast.error('Clé API requise'); return; }
    setSaving(true);
    try {
      const payload = {};
      if (form.api_key) payload.api_key = form.api_key;
      if (form.from_email !== brevo.from_email) payload.from_email = form.from_email;
      if (form.from_name !== brevo.from_name) payload.from_name = form.from_name;
      await api.put('/assos/integrations/brevo', payload);
      toast.success('Brevo configuré');
      setForm({ ...form, api_key: '' });
      onChanged();
    } catch (err) { toast.error(err?.response?.data?.error || 'Erreur'); }
    finally { setSaving(false); }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post('/assos/integrations/brevo/test');
      toast.success(data.message);
    } catch (err) { toast.error(err?.response?.data?.error || 'Erreur'); }
    finally { setTesting(false); }
  };

  const disconnect = async () => {
    if (!window.confirm('Déconnecter Brevo ? Les emails ne pourront plus être envoyés depuis votre asso.')) return;
    try {
      await api.put('/assos/integrations/brevo', { api_key: '', from_email: '', from_name: '' });
      toast.success('Brevo déconnecté');
      onChanged();
    } catch { toast.error('Erreur'); }
  };

  return (
    <form onSubmit={save} className="card mb-4" data-testid="brevo-card">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl grid place-items-center" style={{ background: '#0B996E15', color: '#0B996E' }}>
            <Mail className="w-6 h-6" />
          </div>
          <div>
            <h3 className="font-display text-lg font-black text-[#0F172A]">Brevo · Emails</h3>
            <p className="text-xs text-[#475569]">Envoi des invitations, QR billets, relances aux membres</p>
          </div>
        </div>
        {configured ? (
          <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 flex items-center gap-1" data-testid="brevo-status">
            <CheckCircle2 className="w-3 h-3" /> Connecté
          </span>
        ) : (
          <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-amber-100 text-amber-700 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" /> Non configuré
          </span>
        )}
      </div>

      <div className="bg-[#F9F9F8] rounded-lg p-3 mb-4 text-xs text-[#475569]">
        <p className="font-bold text-[#0F172A] mb-1.5">Étapes :</p>
        <ol className="space-y-0.5 list-decimal pl-4">
          <li>Créez un compte gratuit sur <a href="https://www.brevo.com" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-1">brevo.com <ExternalLink className="w-3 h-3" /></a></li>
          <li>Authentifiez votre domaine d'expédition (SPF/DKIM)</li>
          <li>SMTP & API → Clés API → "Générer une nouvelle clé API"</li>
          <li>Collez la clé ci-dessous (format <code className="bg-white px-1 rounded">xkeysib-...</code>)</li>
        </ol>
      </div>

      <div className="space-y-3">
        <div>
          <label className="label-uppercase block mb-1.5 flex items-center gap-1.5">
            <KeyRound className="w-3 h-3" /> Clé API Brevo {configured && <span className="text-[10px] font-normal text-[#94A3B8] normal-case">(saisissez une nouvelle clé pour remplacer)</span>}
          </label>
          <input
            type="password"
            value={form.api_key}
            onChange={(e) => setForm({ ...form, api_key: e.target.value })}
            className="input-base font-mono text-xs"
            placeholder={configured ? brevo.api_key_masked : 'xkeysib-...'}
            data-testid="brevo-api-key"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="label-uppercase block mb-1.5">Email expéditeur *</label>
            <input
              type="email" required
              value={form.from_email}
              onChange={(e) => setForm({ ...form, from_email: e.target.value })}
              className="input-base"
              placeholder="contact@votre-asso.fr"
              data-testid="brevo-from-email"
            />
          </div>
          <div>
            <label className="label-uppercase block mb-1.5">Nom expéditeur</label>
            <input
              type="text"
              value={form.from_name}
              onChange={(e) => setForm({ ...form, from_name: e.target.value })}
              className="input-base"
              placeholder={asso.nom}
              data-testid="brevo-from-name"
            />
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mt-5 pt-4 border-t border-[#F1F5F9]">
        <button type="submit" disabled={saving} className="btn-primary flex items-center gap-1.5 text-sm" data-testid="brevo-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Enregistrer
        </button>
        {configured && (
          <>
            <button type="button" onClick={sendTest} disabled={testing} className="btn-secondary flex items-center gap-1.5 text-sm" data-testid="brevo-test">
              {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Envoyer un test
            </button>
            <button type="button" onClick={disconnect} className="text-rose-600 hover:text-rose-700 text-sm font-bold ml-auto" data-testid="brevo-disconnect">Déconnecter</button>
          </>
        )}
      </div>
    </form>
  );
}

// ─── STRIPE CARD (BYOK — clé Stripe propre à l'asso) ────────────────────────
function StripeCard({ asso, onChanged }) {
  const stripe = asso.integrations?.stripe || {};
  const configured = stripe.configured;
  const [form, setForm] = useState({ secret_key: '', publishable_key: stripe.publishable_key || '' });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    if (!form.secret_key && !configured) { toast.error('Clé secrète requise'); return; }
    setSaving(true);
    try {
      const payload = {};
      if (form.secret_key) payload.secret_key = form.secret_key;
      if (form.publishable_key !== stripe.publishable_key) payload.publishable_key = form.publishable_key;
      await api.put('/assos/integrations/stripe', payload);
      toast.success('Stripe configuré');
      setForm({ ...form, secret_key: '' });
      onChanged();
    } catch (err) { toast.error(err?.response?.data?.detail || err?.response?.data?.error || 'Erreur'); }
    finally { setSaving(false); }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post('/assos/integrations/stripe/test');
      toast.success(data.message);
      onChanged();
    } catch (err) { toast.error(err?.response?.data?.detail || err?.response?.data?.error || 'Erreur'); }
    finally { setTesting(false); }
  };

  const disconnect = async () => {
    if (!window.confirm('Déconnecter Stripe ? Les adhésions ne pourront plus être réglées en ligne.')) return;
    try {
      await api.put('/assos/integrations/stripe', { secret_key: '' });
      toast.success('Stripe déconnecté');
      onChanged();
    } catch { toast.error('Erreur'); }
  };

  return (
    <form onSubmit={save} className="card mb-4" data-testid="stripe-card">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl grid place-items-center" style={{ background: '#635BFF15', color: '#635BFF' }}>
            <CreditCard className="w-6 h-6" />
          </div>
          <div>
            <h3 className="font-display text-lg font-black text-[#0F172A]">Stripe · Paiements</h3>
            <p className="text-xs text-[#475569]">Encaissez les adhésions par carte, directement sur votre compte Stripe</p>
          </div>
        </div>
        {configured ? (
          <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 flex items-center gap-1" data-testid="stripe-status">
            <CheckCircle2 className="w-3 h-3" /> Connecté
          </span>
        ) : (
          <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-amber-100 text-amber-700 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" /> Non connecté
          </span>
        )}
      </div>

      {configured && (
        <div className="space-y-2 mb-4" data-testid="stripe-connected-info">
          <div className="grid grid-cols-2 gap-2">
            <StatusPill label={`Mode ${stripe.mode || 'test'}`} ok={stripe.mode === 'live'} testid="stripe-pill-mode" />
            <StatusPill label={stripe.charges_enabled ? 'Encaissement actif' : 'Compte à activer'} ok={stripe.charges_enabled} testid="stripe-pill-charges" />
          </div>
          {!stripe.charges_enabled && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 text-[11px] text-amber-800" data-testid="stripe-not-activated">
              Votre compte Stripe n'est pas encore totalement activé. Complétez votre profil sur Stripe (informations bancaires) pour recevoir les paiements.
            </div>
          )}
          <div className="bg-[#F9F9F8] rounded-lg p-3">
            <div className="text-[10px] uppercase tracking-wider text-[#94A3B8] font-bold">Clé secrète enregistrée</div>
            <div className="font-mono text-xs text-[#0F172A] truncate">{stripe.secret_key_masked}</div>
          </div>
        </div>
      )}

      <div className="bg-[#F9F9F8] rounded-lg p-3 mb-4 text-xs text-[#475569]">
        <p className="font-bold text-[#0F172A] mb-1.5">Où trouver vos clés ?</p>
        <ol className="space-y-0.5 list-decimal pl-4">
          <li>Créez / ouvrez votre compte sur <a href="https://dashboard.stripe.com" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-1">stripe.com <ExternalLink className="w-3 h-3" /></a></li>
          <li>Menu <strong>Développeurs → Clés API</strong></li>
          <li>Copiez la <strong>clé secrète</strong> (<code className="bg-white px-1 rounded">sk_live_…</code>) et la <strong>clé publiable</strong> (<code className="bg-white px-1 rounded">pk_live_…</code>)</li>
          <li>Collez-les ci-dessous — elles restent chiffrées et ne sont jamais réaffichées</li>
        </ol>
      </div>

      <div className="space-y-3">
        <div>
          <label className="label-uppercase block mb-1.5 flex items-center gap-1.5">
            <KeyRound className="w-3 h-3" /> Clé secrète Stripe {configured && <span className="text-[10px] font-normal text-[#94A3B8] normal-case">(saisissez une nouvelle clé pour remplacer)</span>}
          </label>
          <input
            type="password"
            value={form.secret_key}
            onChange={(e) => setForm({ ...form, secret_key: e.target.value })}
            className="input-base font-mono text-xs"
            placeholder={configured ? stripe.secret_key_masked : 'sk_live_...'}
            data-testid="stripe-secret-key"
          />
        </div>
        <div>
          <label className="label-uppercase block mb-1.5">Clé publiable (optionnelle)</label>
          <input
            type="text"
            value={form.publishable_key}
            onChange={(e) => setForm({ ...form, publishable_key: e.target.value })}
            className="input-base font-mono text-xs"
            placeholder="pk_live_..."
            data-testid="stripe-publishable-key"
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mt-5 pt-4 border-t border-[#F1F5F9]">
        <button type="submit" disabled={saving} className="btn-primary flex items-center gap-1.5 text-sm" data-testid="stripe-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Enregistrer
        </button>
        {configured && (
          <>
            <button type="button" onClick={sendTest} disabled={testing} className="btn-secondary flex items-center gap-1.5 text-sm" data-testid="stripe-test">
              {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Vérifier la connexion
            </button>
            <button type="button" onClick={disconnect} className="text-rose-600 hover:text-rose-700 text-sm font-bold ml-auto" data-testid="stripe-disconnect">Déconnecter</button>
          </>
        )}
      </div>
    </form>
  );
}

function StatusPill({ label, ok, testid }) {
  return (
    <div className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs ${ok ? 'bg-emerald-50 text-emerald-700' : 'bg-[#F1F5F9] text-[#475569]'}`} data-testid={testid}>
      {ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />}
      <span className="font-bold">{label}</span>
    </div>
  );
}
